import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, formatApiError } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Eye, EyeOff, ArrowRight, Search } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets-4nw71qhi.emergentagent.net/job_school-mgmt-72/artifacts/eluovc6p_ChatGPT%20Image%20Sep%2015%2C%202026%2C%2009_07_38%20AM.png';
const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/4bff2a47-fb36-4a8a-96df-3abd338c4e14/images/441b0bf3c2f0dce8ebaeb872aabf249b7bef06a3cfc9b7abbd663c87c240abc2.jpeg';
const roleRedirects = { admin: '/admin', teacher: '/teacher', student: '/student', parent: '/parent' };

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!email || !password) { setError('Please enter email and password'); return; }
    setLoading(true); setError('');
    try {
      const data = await login(email, password);
      toast.success(`Welcome back, ${data.name}!`);
      navigate(roleRedirects[data.role] || '/');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="login-page">
      {/* Warm amber background matching site design */}
      <div className="absolute inset-0 bg-[#F5D882]" />
      <div className="absolute top-[-60px] left-[-40px] w-[320px] h-[320px] opacity-60">
        <svg viewBox="0 0 300 300"><ellipse cx="150" cy="150" rx="130" ry="110" fill="#E8913A" /><path d="M80,60 Q150,20 220,80 Q200,40 140,50Z" fill="#3B7D4F" /></svg>
      </div>
      <div className="absolute bottom-[-80px] right-[-50px] w-[350px] h-[300px] opacity-50">
        <svg viewBox="0 0 350 300"><path d="M50,250 Q20,150 100,80 Q180,20 260,80 Q340,150 300,250Z" fill="#3B7D4F" /><ellipse cx="280" cy="100" rx="50" ry="70" fill="#E8913A" opacity="0.6" /></svg>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-[1000px]">
          <div className="bg-white rounded-[2rem] shadow-2xl shadow-amber-900/10 overflow-hidden">
            <div className="grid lg:grid-cols-2">
              {/* Left - Branding panel */}
              <div className="hidden lg:flex flex-col justify-between p-10 bg-[#1B2A4A] relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#E8A830]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#E8A830]/5 rounded-full translate-y-1/2 -translate-x-1/2" />
                <div className="relative z-10">
                  <img src={LOGO_URL} alt="Tadam Academy" className="w-16 h-16 object-contain mb-6" />
                  <h1 className="font-display text-3xl font-black text-white mb-2">Tadam Academy</h1>
                  <p className="text-[#E8A830] text-sm font-bold tracking-wider mb-1">Mwana wako mwana wangu.</p>
                  <p className="text-slate-400 text-xs">Your child is my child</p>
                </div>
                <div className="relative z-10 mt-8">
                  <img src={HERO_IMG} alt="Students" className="w-full max-w-[280px] rounded-2xl opacity-80" />
                </div>
                <p className="relative z-10 text-slate-500 text-xs mt-6">Pre-School | Primary | High School<br />ECD through A-Level - Harare, Zimbabwe</p>
              </div>

              {/* Right - Login form */}
              <div className="p-8 sm:p-10 lg:p-12 flex flex-col justify-center">
                <div className="lg:hidden flex items-center gap-3 mb-8">
                  <img src={LOGO_URL} alt="Tadam Academy" className="w-10 h-10 object-contain" />
                  <div>
                    <span className="font-bold text-[#1B2A4A]">Tadam Academy</span>
                    <p className="text-[9px] text-[#E8A830] font-bold">Mwana wako mwana wangu</p>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-[#1B2A4A] mb-1">Portal Login</h2>
                <p className="text-slate-500 text-sm mb-6">Sign in to access your personalised portal</p>

                {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-5 text-sm text-red-700" data-testid="login-error">{error}</div>}

                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">Email Address</Label>
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1 h-11" data-testid="login-email-input" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">Password</Label>
                    <div className="relative mt-1">
                      <Input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" className="h-11 pr-10" data-testid="login-password-input" />
                      <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-[#E8A830] hover:bg-[#D4982A] text-white font-bold" data-testid="login-submit-button">
                    {loading ? 'Signing in...' : 'Sign In'} {!loading && <ArrowRight className="w-4 h-4 ml-1" />}
                  </Button>
                </form>

                <div className="mt-6 pt-5 border-t border-slate-100">
                  <p className="text-xs text-slate-400 mb-3 font-semibold uppercase tracking-wider">Portals available for:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Admin', desc: 'School management', color: 'bg-indigo-50 text-indigo-700 border-indigo-100' },
                      { label: 'Teacher', desc: 'Classes & grading', color: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
                      { label: 'Student', desc: 'Timetable & results', color: 'bg-sky-50 text-sky-700 border-sky-100' },
                      { label: 'Parent', desc: 'Child overview', color: 'bg-amber-50 text-amber-700 border-amber-100' },
                    ].map(p => (
                      <div key={p.label} className={`rounded-lg border p-2.5 ${p.color}`}>
                        <p className="text-xs font-bold">{p.label}</p>
                        <p className="text-[10px] opacity-70">{p.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-center text-sm text-slate-500 mt-6">
                  <Link to="/" className="text-[#1B2A4A] font-semibold hover:underline">&larr; Back to website</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
